import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'

export interface LoggedInUserData {
    id:       string
    name:     string
    username: string
}

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'en')
    const clientName   = ref(localStorage.getItem('clientName') || '')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<LoggedInUserData>()

    // Keep windowWidth live so isMobile() reflects the current viewport, not just the width at
    // first store instantiation. The app store lives for the page's lifetime, so the listener
    // never needs removing.
    window.addEventListener('resize', () => {
        windowWidth.value = window.innerWidth
    })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    // Mirror the JWT into a cookie so requests for images/PDFs loaded via <img src>/<a href>
    // (which cannot carry an Authorization header) are still authenticated.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)
    }

    // The active client (merchant) picked at the client step, for the shell to display.
    const setClient = (name: string) => {
        clientName.value = name
        localStorage.setItem('clientName', name)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        localStorage.removeItem('clientName')
        JWT.value = ''
        clientName.value = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
    }

    return {
        JWT,
        locale,
        clientName,
        userData,
        loaderActive,
        getJWT,
        isLoggedIn,
        isLoaderActive,
        isMobile,
        setLogin,
        setClient,
        setLogout,
        startLoading,
        stopLoading,
        changeLocale,
    }
})
